import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fishBadge } from "@/lib/api";
import WeatherWidget from "@/components/WeatherWidget";
import SpotMap from "@/components/SpotMap";
import { Button } from "@/components/ui/button";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ShareButton } from "@/components/ShareButton";
import { setAISpot, openAssistant } from "@/lib/aiSpot";
import {
  ArrowLeft, MapPin, Star, Waves, Calendar, Gauge, Navigation,
  Lightbulb, IdCard, Fish, Loader2, BookOpen, Sparkles,
} from "lucide-react";

export default function SpotDetail() {
  const { id } = useParams();
  const [spot, setSpot] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/spots/${id}`).then((r) => setSpot(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    setAISpot(spot);
    return () => setAISpot(null);
  }, [spot]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-xl font-display font-bold text-emerald-950">Spot introuvable</p>
        <Link to="/spots" className="text-emerald-700 font-semibold mt-3 inline-block">← Retour à la carte</Link>
      </div>
    );

  if (!spot)
    return (
      <div className="grid place-items-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );

  return (
    <div>
      {/* header image */}
      <div className="relative h-[42vh] min-h-[320px]">
        <img src={spot.image} alt={spot.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 sm:px-8 pb-8">
          <Link
            to="/spots"
            data-testid="back-to-spots"
            className="inline-flex items-center gap-1 text-emerald-100 text-sm font-semibold mb-4 hover:gap-2 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à la carte
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="flex items-center gap-1 bg-white/90 px-3 py-1 rounded-full text-xs font-semibold text-emerald-900">
              <Waves className="w-3.5 h-3.5 text-sky-600" /> {spot.water_type}
            </span>
            <span className="flex items-center gap-1 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              <Star className="w-3.5 h-3.5 fill-white" /> {spot.rating}
            </span>
            <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-semibold">{spot.difficulty}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{spot.name}</h1>
          <p className="flex items-center gap-1.5 text-emerald-100/90 mt-2">
            <MapPin className="w-4 h-4" /> {spot.department} · {spot.region}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <FavoriteButton spotId={spot.id} withLabel />
            <ShareButton spot={spot} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-[1fr_380px] gap-8">
        {/* main */}
        <div className="space-y-8">
          <section>
            <h2 className="font-display text-xl font-bold text-emerald-950 mb-2">À propos du spot</h2>
            <p className="text-slate-700 leading-relaxed">{spot.description}</p>
          </section>

          <section>
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700/80 flex items-center gap-2 mb-3">
              <Fish className="w-4 h-4" /> Espèces présentes
            </p>
            <div className="flex flex-wrap gap-2">
              {spot.fish.map((f) => (
                <span key={f} className={`text-sm font-semibold px-3 py-1.5 rounded-full ${fishBadge(f)}`}>{f}</span>
              ))}
            </div>
          </section>

          <section className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 flex items-center gap-3">
              <Calendar className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700/70">Meilleure saison</p>
                <p className="font-semibold text-emerald-950">{spot.best_season}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 flex items-center gap-3">
              <Gauge className="w-6 h-6 text-amber-600" />
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700/70">Niveau</p>
                <p className="font-semibold text-emerald-950">{spot.difficulty}</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex gap-3">
            <Lightbulb className="w-6 h-6 text-amber-600 shrink-0" />
            <div>
              <p className="font-semibold text-amber-900">Conseil du guide</p>
              <p className="text-sm text-amber-800/90 mt-1">{spot.tips}</p>
              <button
                type="button"
                onClick={openAssistant}
                data-testid="spot-ask-ai"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:gap-2.5 transition-all"
              >
                <Sparkles className="w-4 h-4" /> Demander conseil à PêcheAI pour ce spot
              </button>
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-emerald-950 mb-3">Localisation</h2>
            <div className="h-72 rounded-2xl overflow-hidden border border-emerald-900/10">
              <SpotMap spots={[spot]} activeId={spot.id} height="100%" />
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${spot.lat},${spot.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="spot-directions"
              className="inline-flex items-center gap-2 mt-3 text-emerald-700 font-semibold text-sm hover:gap-3 transition-all"
            >
              <Navigation className="w-4 h-4" /> Itinéraire (GPS : {spot.lat.toFixed(3)}, {spot.lng.toFixed(3)})
            </a>
          </section>
        </div>

        {/* sidebar */}
        <div className="space-y-5 lg:sticky lg:top-24 self-start">
          <WeatherWidget lat={spot.lat} lon={spot.lng} />

          <div className="rounded-2xl bg-emerald-900 text-white p-6">
            <IdCard className="w-8 h-8 text-amber-400 mb-2" />
            <p className="font-display font-bold text-lg">Carte de pêche requise</p>
            <p className="text-sm text-emerald-100/80 mt-1">
              Une carte valide est obligatoire pour pêcher sur ce spot ({spot.water_type}).
            </p>
            <a
              href="https://www.cartedepeche.fr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="spot-buy-license"
              className="mt-4 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-emerald-950 font-bold py-3 rounded-full transition-all"
            >
              Acheter ma carte
            </a>
          </div>

          <Link to="/equipement" data-testid="spot-see-kits" className="block">
            <Button className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6 text-base font-semibold">
              Préparer mon équipement →
            </Button>
          </Link>
          <Link
            to={`/carnet?spot=${spot.id}`}
            data-testid="spot-log-catch"
            className="flex items-center justify-center gap-2 w-full border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50 rounded-full py-3 font-semibold transition-colors"
          >
            <BookOpen className="w-4 h-4" /> Noter une prise ici
          </Link>
        </div>
      </div>
    </div>
  );
}
